import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import classnames from 'classnames';

// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {ListView} from 'shells/list-view/list-view';
import {Notification} from 'shells/notifications/notification';

import type {GetNotifications_notifications as NotificationType} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';

import {getAvatar, getHtmlUrl, shouldOpenActivityModal} from './helpers';

import styles from './notification-list.module.css';

type Props = {
    notifications: NotificationType[];
    isLoading: boolean;
    errorMessage: string | null | undefined;
    onMarkRead: (arg1: string) => any;
    closeSidebar: () => any;
};

export function NotificationList(props: Props) {
    const {user} = useGetSessionUser();

    return (
        <ListView
            collection={props.notifications}
            renderRow={(notification: NotificationType) => {
                return (
                    <Notification
                        graphic={getAvatar(notification)}
                        content={notification.content}
                        preview={notification.preview}
                        isRead={notification.readStatus === 'READ'}
                        createdTime={notification.createdTime}
                        onMarkRead={() => {
                            props.onMarkRead(notification.id);
                        }}
                        onNavigate={() => {
                            props.closeSidebar();
                            const urlInfo = getHtmlUrl(notification);
                            if (shouldOpenActivityModal(notification) && urlInfo) {
                                Modal.open({url: urlInfo.url, onSuccess: {}});
                            } else if (urlInfo) {
                                if (urlInfo.navigationType === 'reload') {
                                    window.location.href = urlInfo.url;
                                } else {
                                    appHistory.push({
                                        search: appHistory.location.search,
                                        ...(urlInfo.useHash
                                            ? {hash: urlInfo.url}
                                            : {pathname: urlInfo.url}),
                                    });
                                }
                            }
                        }}
                        key={notification.id}
                    />
                );
            }}
            loadingState={{
                customComponent: (
                    <div
                        className={classnames(
                            styles['notifications-message'],
                            styles['notifications-message-loading']
                        )}
                    >
                        <LoadingIcon size={40} className={styles['loading-icon']} />
                    </div>
                ),
            }}
            isLoading={props.isLoading}
            specialStates={[
                {
                    // Empty state
                    shouldRender: () =>
                        Boolean(
                            props.notifications.length === 0 &&
                                !props.isLoading &&
                                !props.errorMessage
                        ),
                    component: (
                        <div
                            className={classnames(
                                styles['notifications-message'],
                                styles['notifications-message-empty']
                            )}
                        >
                            <img
                                src='/include/images/popovers/notification-empty-state-graphic.svg'
                                className={styles['empty-state-graphic']}
                            />
                            <h1>No notifications&hellip; yet!</h1>
                            {user && user.permissions.canAccessCrm ? (
                                <p>
                                    Change your{' '}
                                    <Link
                                        // @ts-expect-error
                                        as={RoutedLink}
                                        variant='primary'
                                        size='small'
                                        to='/my-account/notifications'
                                        onClick={props.closeSidebar}
                                    >
                                        notification settings
                                    </Link>{' '}
                                    to receive in-app notifications!
                                </p>
                            ) : undefined}
                        </div>
                    ),
                },
                {
                    // Error state
                    shouldRender: () => Boolean(props.errorMessage),
                    component: (
                        <div
                            className={classnames(
                                styles['notifications-message'],
                                styles['notifications-message-loading']
                            )}
                        >
                            <div style={{width: '150px'}}>
                                <Illustration src='plug-error-state' width='100%' height='100%' />
                                <p>There was an error retrieving notifications.</p>
                            </div>
                        </div>
                    ),
                },
            ]}
        />
    );
}
